import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fastapiService } from '../../../services/fastapiService';
import { useAdminBasePath } from '../../../hooks/useAdminBasePath';
import type { AdminWorkspaceSubscriptionRow } from '../../../types';
import { SubscriptionStatusBadge } from './SubscriptionStatusBadge';

function toDateInputValue(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function toIsoEndOfDay(dateStr: string): string | null {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T23:59:59.999Z`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** Format stored INR override for the workspace billing-currency input. */
function overrideInrToInputValue(
  overrideInr: number | null | undefined,
  displayCurrency: string,
  usdInrRate: number
): string {
  if (overrideInr == null || Number.isNaN(overrideInr)) return '';
  if (displayCurrency === 'USD') {
    return String(Math.round((overrideInr / usdInrRate) * 100) / 100);
  }
  return String(overrideInr);
}

interface SubscriptionEditModalProps {
  row: AdminWorkspaceSubscriptionRow;
  onClose: () => void;
  onSaved: () => void;
}

export const SubscriptionEditModal: React.FC<SubscriptionEditModalProps> = ({
  row,
  onClose,
  onSaved,
}) => {
  const basePath = useAdminBasePath();
  const sub = row.subscription;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedState, setSavedState] = useState<string | null>(null);

  const [renewalDate, setRenewalDate] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [graceEnd, setGraceEnd] = useState('');
  const [trialEnd, setTrialEnd] = useState('');
  const [status, setStatus] = useState('active');
  const [reason, setReason] = useState('');

  const [priceOverride, setPriceOverride] = useState<string>('');
  const [annualDiscount, setAnnualDiscount] = useState<string>('');

  const displayCurrency = (
    row.pricing.display_currency ||
    (row.workspace.billing_currency === 'USD' ? 'USD' : 'INR')
  ).toUpperCase();
  const isUsdBilling = displayCurrency === 'USD';

  /** Same INR→USD rate the subscriptions list API uses for override conversion. */
  const usdInrRate = useMemo(() => {
    const fromApi = row.pricing.usd_inr_rate;
    if (typeof fromApi === 'number' && fromApi > 0) return fromApi;
    return 83;
  }, [row.pricing.usd_inr_rate]);

  const initialPricing = useMemo(
    () => ({
      priceOverride: overrideInrToInputValue(
        row.pricing.monthly_subscription_price_override,
        displayCurrency,
        usdInrRate
      ),
      annualDiscount:
        row.pricing.annual_discount_percentage > 0
          ? String(row.pricing.annual_discount_percentage)
          : '',
    }),
    [row, displayCurrency, usdInrRate]
  );

  useEffect(() => {
    if (!sub) return;
    setRenewalDate(toDateInputValue(sub.current_period_end));
    setPeriodStart(toDateInputValue(sub.current_period_start));
    setGraceEnd(toDateInputValue(sub.grace_end || row.computed.grace_end_effective));
    setTrialEnd(toDateInputValue(sub.trial_ends_at));
    setStatus(sub.status || 'active');
    setPriceOverride(initialPricing.priceOverride);
    setAnnualDiscount(initialPricing.annualDiscount);
    setSavedState(null);
    setError(null);
  }, [sub, row, initialPricing]);

  const effectivePreview = useMemo(() => {
    const overrideNum = priceOverride.trim() === '' ? null : parseFloat(priceOverride);
    if (overrideNum != null && !Number.isNaN(overrideNum)) {
      // Input is already in the workspace billing currency
      return overrideNum;
    }
    return isUsdBilling
      ? (row.pricing.effective_monthly_price_usd ?? row.pricing.effective_monthly_price)
      : (row.pricing.effective_monthly_price_inr ?? row.pricing.effective_monthly_price);
  }, [priceOverride, row, isUsdBilling]);

  const storedInrPreview = useMemo(() => {
    const overrideNum = priceOverride.trim() === '' ? null : parseFloat(priceOverride);
    if (overrideNum == null || Number.isNaN(overrideNum) || !isUsdBilling) return null;
    return Math.round(overrideNum * usdInrRate * 100) / 100;
  }, [priceOverride, isUsdBilling, usdInrRate]);

  const pricingChanged =
    priceOverride !== initialPricing.priceOverride ||
    annualDiscount !== initialPricing.annualDiscount;

  const handleResetGrace = () => {
    setGraceEnd('');
  };

  const handleSave = async () => {
    if (!sub) return;
    setLoading(true);
    setError(null);
    try {
      const lifecyclePayload: Record<string, unknown> = {};
      const renewalIso = toIsoEndOfDay(renewalDate);
      const startIso = toIsoEndOfDay(periodStart);
      const graceIso = graceEnd ? toIsoEndOfDay(graceEnd) : null;
      const trialIso = trialEnd ? toIsoEndOfDay(trialEnd) : null;

      if (renewalIso !== (sub.current_period_end || null)) {
        lifecyclePayload.current_period_end = renewalIso;
      }
      if (periodStart && startIso !== (sub.current_period_start || null)) {
        lifecyclePayload.current_period_start = startIso;
      }
      if (graceEnd !== toDateInputValue(sub.grace_end || row.computed.grace_end_effective)) {
        lifecyclePayload.grace_end = graceIso;
      } else if (!graceEnd && sub.grace_end) {
        lifecyclePayload.grace_end = null;
      }
      if (status !== sub.status) lifecyclePayload.status = status;
      if ((sub.status || '').toLowerCase() === 'trialing' || status === 'trialing') {
        if (trialIso !== (sub.trial_ends_at || null)) lifecyclePayload.trial_ends_at = trialIso;
      }
      if (reason.trim()) lifecyclePayload.reason = reason.trim();

      if (Object.keys(lifecyclePayload).length > 0) {
        const res = await fastapiService.updateSubscription(sub.id, lifecyclePayload);
        const computed = (res as any)?.data?.computed?.computed_state;
        if (computed) setSavedState(computed);
      }

      if (pricingChanged) {
        const pricingBody: Record<string, unknown> = {
          is_active: true,
          override_currency: isUsdBilling ? 'USD' : 'INR',
        };
        if (priceOverride.trim() === '') {
          pricingBody.monthly_subscription_price_override = null;
        } else {
          const n = parseFloat(priceOverride);
          if (!Number.isNaN(n)) pricingBody.monthly_subscription_price_override = n;
        }
        if (annualDiscount.trim() === '') {
          pricingBody.annual_discount_percentage = 0;
        } else {
          const d = parseFloat(annualDiscount);
          if (!Number.isNaN(d)) pricingBody.annual_discount_percentage = d;
        }
        await fastapiService.updateWorkspaceSubscriptionPricing(row.workspace.id, pricingBody);
      }

      onSaved();
      if (!Object.keys(lifecyclePayload).length && !pricingChanged) {
        setError('No changes to save');
        return;
      }
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  if (!sub) {
    const ownerId = row.workspace.owner?.id;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="admin-card w-full max-w-md p-6">
          <div className="flex items-start justify-between mb-4">
            <h2 className="text-lg font-semibold text-admin-900">No subscription</h2>
            <button type="button" onClick={onClose} className="text-admin-500 hover:text-admin-700">
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-sm text-admin-600 mb-4">
            <strong>{row.workspace.name}</strong> has no workspace plan subscription.
          </p>
          {ownerId ? (
            <Link
              to={`${basePath}/users/${ownerId}`}
              className="text-sm font-medium text-primary-600 hover:text-primary-800"
              onClick={onClose}
            >
              Enroll via user profile →
            </Link>
          ) : (
            <p className="text-sm text-admin-500">No workspace owner on file.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto">
      <div className="admin-card w-full max-w-lg p-6 my-8">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-admin-900">Edit subscription</h2>
            <p className="text-sm text-admin-600">{row.workspace.name}</p>
          </div>
          <button type="button" onClick={onClose} className="text-admin-500 hover:text-admin-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && <div className="mb-3 text-sm text-red-600">{error}</div>}

        <div className="mb-4 flex items-center gap-2">
          <span className="text-xs text-admin-500 uppercase">Current state</span>
          <SubscriptionStatusBadge state={savedState || row.computed.computed_state} />
        </div>

        <div className="space-y-6">
          <section>
            <h3 className="text-sm font-medium text-admin-900 mb-3">Subscription lifecycle</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-admin-500 mb-1">Renewal date</label>
                <input
                  type="date"
                  value={renewalDate}
                  onChange={(e) => setRenewalDate(e.target.value)}
                  className="admin-input w-full"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-admin-500 mb-1">Period start (optional)</label>
                <input
                  type="date"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                  className="admin-input w-full"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-admin-500 mb-1">Grace end</label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={graceEnd}
                    onChange={(e) => setGraceEnd(e.target.value)}
                    className="admin-input flex-1"
                  />
                  <button type="button" className="admin-button text-xs whitespace-nowrap" onClick={handleResetGrace}>
                    Default (5d)
                  </button>
                </div>
                <p className="mt-1 text-xs text-admin-500">Clear to use default 5 days after renewal.</p>
              </div>
              {(status === 'trialing' || (sub.status || '').toLowerCase() === 'trialing') && (
                <div>
                  <label className="block text-xs font-medium text-admin-500 mb-1">Trial end</label>
                  <input
                    type="date"
                    value={trialEnd}
                    onChange={(e) => setTrialEnd(e.target.value)}
                    className="admin-input w-full"
                  />
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-admin-500 mb-1">Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="admin-input w-full">
                  <option value="active">Active</option>
                  <option value="trialing">Trialing</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
              <div className="rounded-md bg-admin-50 px-3 py-2 text-sm text-admin-700">
                Auto-pay:{' '}
                <strong>
                  {sub.autopay_enabled
                    ? `On (${sub.autopay_status || 'active'}${sub.autopay_currency ? `, ${sub.autopay_currency}` : ''})`
                    : sub.autopay_status
                      ? `Off (${sub.autopay_status})`
                      : 'Off'}
                </strong>
                <p className="mt-1 text-xs text-admin-500">Read-only. Users manage auto-pay in the CRM.</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-admin-500 mb-1">Reason (audit log)</label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Optional note for audit trail"
                  className="admin-input w-full"
                />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-sm font-medium text-admin-900 mb-3">Pricing &amp; discounts</h3>
            <div className="space-y-3">
              <div className="rounded-md bg-admin-50 px-3 py-2 text-sm text-admin-700 space-y-1">
                <div>
                  Tier: <strong>{row.pricing.tier_name}</strong>
                </div>
                <div>
                  Base:{' '}
                  <strong>
                    {displayCurrency === 'USD'
                      ? (row.pricing.base_monthly_price_usd ?? 0).toLocaleString('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : (row.pricing.base_monthly_price_inr ?? row.pricing.base_monthly_price ?? 0).toLocaleString(
                          'en-IN',
                          { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }
                        )}
                  </strong>
                </div>
                <div className="text-xs text-admin-500">
                  Billing currency: {displayCurrency}
                  {row.workspace.billing_currency &&
                    row.workspace.billing_currency.toUpperCase() !== displayCurrency && (
                      <span> (workspace setting: {row.workspace.billing_currency})</span>
                    )}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-admin-500 mb-1">
                  Custom monthly price ({displayCurrency})
                </label>
                <input
                  type="number"
                  min={0}
                  step={isUsdBilling ? 0.01 : 1}
                  value={priceOverride}
                  onChange={(e) => setPriceOverride(e.target.value)}
                  placeholder={
                    isUsdBilling
                      ? 'e.g. 350 — leave blank for tier price'
                      : 'e.g. 29500 — leave blank for tier price'
                  }
                  className="admin-input w-full"
                />
                <p className="mt-1 text-xs text-admin-500">
                  {isUsdBilling
                    ? 'Enter the USD amount charged for this international workspace.'
                    : 'Enter the INR amount charged for this India workspace.'}
                </p>
                {storedInrPreview != null && (
                  <p className="mt-1 text-xs text-admin-500">
                    Stored internally as{' '}
                    {storedInrPreview.toLocaleString('en-IN', {
                      style: 'currency',
                      currency: 'INR',
                      maximumFractionDigits: 0,
                    })}{' '}
                    (rate {usdInrRate})
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-admin-500 mb-1">Annual discount %</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  step={0.1}
                  value={annualDiscount}
                  onChange={(e) => setAnnualDiscount(e.target.value)}
                  className="admin-input w-full"
                />
              </div>
              <div className="text-sm text-admin-700">
                Effective monthly:{' '}
                <strong>
                  {effectivePreview.toLocaleString(isUsdBilling ? 'en-US' : 'en-IN', {
                    style: 'currency',
                    currency: displayCurrency,
                    maximumFractionDigits: isUsdBilling ? 2 : 0,
                  })}
                </strong>
              </div>
            </div>
          </section>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className="admin-button" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button type="button" className="admin-button-primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};
